import React, { FC, useState } from 'react';
import { Tooltip } from 'antd';
import Modal from '../overlay/Modal';
import { renderOptions } from '../form/selectOptions';
import { useToast } from '../toast/ToastProvider';
import { ActiveSubscription, extendTrial } from '../../requests/extend.trial.request';
import { MAX_TRIAL_DAYS_REMAINING, getMaxTrialExtension } from '../../utils/trial.helper';

type ExtendTrialProps = {
  shopId: string;
  daysRemaining: number;
  onExtended: (subscription: ActiveSubscription) => void;
};

const plural = (days: number) => `${days} ${days === 1 ? 'day' : 'days'}`;

const errorMessage = (error: unknown) => {
  const message = (error as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof message === 'string' ? message : 'Could not extend the trial.';
};

const ExtendTrial: FC<ExtendTrialProps> = ({ shopId, daysRemaining, onExtended }) => {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState('1');
  const [saving, setSaving] = useState(false);
  const maxDays = getMaxTrialExtension(daysRemaining);
  const atLimit = maxDays === 0;

  const show = () => {
    setDays('1');
    setOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { subscription, billing_error: billingError } = await extendTrial(shopId, Number(days));
      onExtended(subscription);
      if (billingError) {
        toast.show(`Trial extended by ${plural(Number(days))} in Shopify, but ${billingError}`,
          { isError: true, duration: 10000 });
      } else {
        toast.show(`Trial extended by ${plural(Number(days))}`);
      }
      setOpen(false);
    } catch (error) {
      toast.show(errorMessage(error), { isError: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {atLimit ? (
        <Tooltip title={`The trial already has the maximum of ${MAX_TRIAL_DAYS_REMAINING} days left`}>
          {/* A disabled button gets no hover events, so the tooltip sits on a focusable wrapper. */}
          <span tabIndex={0}>
            <s-button variant='tertiary' disabled>Extend trial</s-button>
          </span>
        </Tooltip>
      ) : (
        <s-button variant='tertiary' onClick={show}>Extend trial</s-button>
      )}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        heading='Extend trial'
        primaryAction={{ content: 'Extend trial', onAction: submit, loading: saving }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setOpen(false) }]}
      >
        <s-stack gap='base'>
          <s-select label='Extend by' value={days} onChange={(event) => setDays(event.currentTarget.value)}>
            {renderOptions(Array.from({ length: maxDays }, (_, index) => {
              const value = index + 1;
              return { label: plural(value), value: String(value) };
            }))}
          </s-select>
          <s-text>
            {plural(daysRemaining)} left now; {plural(daysRemaining + Number(days))} after extending.
            A trial can't have more than {MAX_TRIAL_DAYS_REMAINING} days left.
          </s-text>
        </s-stack>
      </Modal>
    </>
  );
};

export default ExtendTrial;
